import { useMemo } from "react";
import { theme, Switch, Typography } from "antd";
import {
  LineChartOutlined,
  AppstoreOutlined,
  ClockCircleOutlined,
  BookOutlined,
  TagsOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { FilterTag, makeFilterToggle } from "src/components/ListPage";
import { EntityTypes } from "@vzbl/integrations";
import { motion, AnimatePresence } from "framer-motion";

interface CategorizedTagsProps {
  availableTags: string[];
  libraryFilter: string;
  setLibraryFilter: (filter: string) => void;
  style?: React.CSSProperties;
}

export function CategorizedTags({
  availableTags,
  libraryFilter,
  setLibraryFilter,
  style,
}: CategorizedTagsProps) {
  const { token } = theme.useToken();

  const categoryIcons = {
    metrics: <LineChartOutlined />,
    "Activity types": <AppstoreOutlined />,
    frequency: <ClockCircleOutlined />,
    Categories: <BookOutlined />,
    other: <TagsOutlined />,
  };

  const tagCategories = useMemo(
    () => ({
      frequency: availableTags.filter((tag) =>
        ["daily", "weekly", "monthly"].some((freq) =>
          tag.toLowerCase().includes(freq)
        )
      ),
      Categories: availableTags.filter((tag) =>
        ["summary", "insight", "stat", "attention"].some((type) =>
          tag.toLowerCase().includes(type)
        )
      ),
      metrics: availableTags.filter(
        (tag) =>
          tag.toLowerCase().includes("metric") ||
          tag.toLowerCase().includes("kpi") ||
          tag.toLowerCase().includes("time") ||
          tag.toLowerCase().includes("cycle") ||
          tag.toLowerCase().includes("score")
      ),
      "Activity types": availableTags.filter((tag) =>
        Object.values(EntityTypes).some((type) =>
          tag.toLowerCase().includes(type.toLowerCase())
        )
      ),
      other: availableTags.filter((tag) => {
        const allOtherCategories = [
          "metric",
          "kpi",
          "score",
          "time",
          "cycle",
          ...Object.values(EntityTypes).map((type) => type.toLowerCase()),
          "daily",
          "weekly",
          "monthly",
          "summary",
          "insight",
          "stat",
          "attention",
        ];
        return !allOtherCategories.some((cat) =>
          tag.toLowerCase().includes(cat)
        );
      }),
    }),
    [availableTags]
  );

  // Normalize tag for filtering (lowercase only, keep spaces)
  const normalizeTag = (tag: string) => tag.toLowerCase().replace(/ /g, "_");

  // Create filter toggle utilities for library filters
  const libraryTagFilters = makeFilterToggle(
    (tag) => `tag:${normalizeTag(tag)}`,
    (filter) => filter.replace(/^tag:/, "")
  );

  const libraryBookmarkFilters = makeFilterToggle(
    () => "in:favorites",
    (filter) => filter.replace(/^in:favorites/, "")
  );

  return (
    <div style={style}>
      <div style={{ marginBottom: "var(--padding-5)" }}>
        <div
          style={{
            marginTop: "var(--padding-1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "flex-start",
            }}
          >
            <StarOutlined />
            <span>Favorites</span>
          </div>
          <Switch
            checked={libraryBookmarkFilters.has(libraryFilter, "true")}
            onChange={(checked) => {
              setLibraryFilter(
                libraryBookmarkFilters.toggle(libraryFilter, "true")
              );
            }}
            size="small"
          />
        </div>
      </div>

      {Object.entries(tagCategories).map(
        ([category, tags]) =>
          tags.length > 0 && (
            <div key={category} style={{ marginBottom: "var(--padding-5)" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "var(--color-foreground-secondary)",
                }}
              >
                {categoryIcons[category as keyof typeof categoryIcons]}
                <Typography.Text style={{ fontSize: "var(--font-size-2)" }}>
                  {category.toUpperCase()}
                </Typography.Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <AnimatePresence mode="popLayout">
                  <motion.div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {tags.map((tag) => (
                      <motion.div
                        key={tag}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 0.2,
                          ease: "easeInOut",
                          exit: { duration: 0.2, ease: "easeInOut" },
                        }}
                      >
                        <FilterTag
                          filter={libraryFilter}
                          filterValue={normalizeTag(tag)}
                          filterLabel={tag}
                          path=""
                          filters={libraryTagFilters}
                          onClick={() => {
                            setLibraryFilter(
                              libraryTagFilters.toggle(
                                libraryFilter,
                                normalizeTag(tag)
                              )
                            );
                          }}
                          style={{ marginLeft: 6 }}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )
      )}
    </div>
  );
}
